import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData } from '@trinity/chat';
import { Edit2 } from 'react-feather';
import { KPIWidgetSettings } from './KPIWidgetSettings';

interface KPIWidgetSettingsButtonProps {
  currentChart: QuickChartData | null;
  onSettingsSave: (updatedChart: QuickChartData & { type: 'kpi-widget' }) => void;
}

export const KPIWidgetSettingsButton = ({ currentChart, onSettingsSave }: KPIWidgetSettingsButtonProps) => {
  // KPI Widget Settings Modal State
  const [kpiWidgetSettingsOpen, setKpiWidgetSettingsOpen] = useState(false);
  const [currentKpiWidgetChart, setCurrentKpiWidgetChart] = useState<QuickChartData & { type: 'kpi-widget' } | null>(null);

  const openKpiWidgetSettings = useCallback(() => {
    if (currentChart?.type === 'kpi-widget') {
      setCurrentKpiWidgetChart(currentChart);
      setKpiWidgetSettingsOpen(true);
    }
  }, [currentChart]);

  const closeKpiWidgetSettings = useCallback(() => {
    setKpiWidgetSettingsOpen(false);
    setCurrentKpiWidgetChart(null);
  }, []);

  const handleKpiWidgetSettingsSave = useCallback((updatedChart: QuickChartData & { type: 'kpi-widget' }) => {
    onSettingsSave(updatedChart);
    closeKpiWidgetSettings();
  }, [onSettingsSave, closeKpiWidgetSettings]);

  // Only show button for KPI widget charts
  if (!currentChart || currentChart.type !== 'kpi-widget') {
    return null;
  }

  return (
    <>
      <EditButton onClick={openKpiWidgetSettings} title="Edit KPI Widget Settings">
        <StyledIcon />
      </EditButton>

      {kpiWidgetSettingsOpen && currentKpiWidgetChart && (
        <KPIWidgetSettings
          open={kpiWidgetSettingsOpen}
          onClose={closeKpiWidgetSettings}
          chartData={currentKpiWidgetChart}
          onSave={handleKpiWidgetSettingsSave} />
      )}
    </>
  );
};

const EditButton = styled.div`
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #666;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`;

const StyledIcon = styled(Edit2)(({ theme }) => ({
  width: 22,
  height: 22,
  display: 'flex',
  flexShrink: 0,
  strokeWidth: 3,
  color: theme.palette.wl?.enabled
    ? theme.palette.wl.primary.main
    : theme.palette.sentiment.primary.main,
}));